import React, { Component } from 'react';
import PropTypes from 'prop-types';
import MatchDetailPlayersTeamSwitch from '../MatchDetailPlayersTeamSwitch/MatchDetailPlayersTeamSwitch';
import MatchDetailPlayersSection from '../MatchDetailPlayersSection/MatchDetailPlayersSection';
import MatchDetailPlayersLegend from '../MatchDetailPlayersLegend/MatchDetailPlayersLegend';
import { getMatchesStatistics } from '../../../services/getMatchesStatistics';
import { buildPlayersData } from '../../../utils';
import './_MatchDetailPlayers.scss';

const DEFAULT_STAT_KEYS = ['G', 'PAG', 'TT', 'PT', 'EG', 'FJ', 'RE', 'PPA'];

const DEFAULT_LEGEND = [
  { key: 'G', label: 'Goles' },
  { key: 'PAG', label: 'Pases a gol' },
  { key: 'TT', label: 'Tiros totales' },
  { key: 'PT', label: 'Pases totales' },
  { key: 'EG', label: 'Entradas ganadas' },
  { key: 'FJ', label: 'Fuera de juego' },
  { key: 'RE', label: 'Regates exitosos' },
  { key: 'PPA', label: 'Precisión de pases' },
  { key: 'PP', label: 'Paradas del portero' },
  { key: 'TAC', label: 'Tiros al arco en contra' }
];

const DEFAULT_STATS = {
  G: 8,
  PAG: 8,
  TT: 8,
  PT: 8,
  EG: 8,
  FJ: 8,
  RE: 8,
  PPA: 8
};

const DEFAULT_PLAYER_BASE = {
  number: 34,
  name: 'Nombre de jugador',
  photo: ''
};

const getDefaultSectionsByTeam = () => [];

const DEFAULT_PLAYERS_DATA = {
  teams: [],
  selectedTeamId: null,
  playersByTeam: {},
  legend: DEFAULT_LEGEND,
  statKeys: DEFAULT_STAT_KEYS
};


const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const normalizeStats = (stats, statKeys) => {
  const safeStats = isObject(stats) ? stats : {};
  const normalized = {};

  statKeys.forEach((key) => {
    normalized[key] = safeStats[key] !== undefined ? safeStats[key] : DEFAULT_STATS[key] || 8;
  });

  return normalized;
};

const normalizePlayer = (player, index, statKeys) => {
  const safePlayer = isObject(player) ? player : {};
  const id = safePlayer.id || `player-${index}`;
  const number = safePlayer.number !== undefined ? safePlayer.number : DEFAULT_PLAYER_BASE.number;
  const name = safePlayer.name || DEFAULT_PLAYER_BASE.name;
  const photo = safePlayer.photo || safePlayer.avatar || safePlayer.image || DEFAULT_PLAYER_BASE.photo;

  return {
    id,
    number,
    name,
    photo,
    stats: normalizeStats(safePlayer.stats, statKeys)
  };
};

const normalizePlayersArray = (players, statKeys) => {
  if (!Array.isArray(players)) return [];
  return players.map((player, index) => normalizePlayer(player, index, statKeys));
};

const normalizeSections = (sections, statKeys) => {
  if (!Array.isArray(sections)) return [];

  return sections.map((section, index) => {
    const safeSection = isObject(section) ? section : {};

    return {
      role: safeSection.role || `Sección ${index + 1}`,
      players: normalizePlayersArray(safeSection.players, statKeys)
    };
  });
};

const normalizePlayersData = (playersProp) => {
  if (Array.isArray(playersProp)) {
    return {
      teams: [{ id: 'players', label: 'Jugadores' }],
      selectedTeamId: 'players',
      playersByTeam: {
        players: [{ role: 'Jugadores', players: normalizePlayersArray(playersProp, DEFAULT_STAT_KEYS) }]
      },
      legend: DEFAULT_LEGEND,
      statKeys: DEFAULT_STAT_KEYS
    };
  }

  if (!isObject(playersProp)) {
    return DEFAULT_PLAYERS_DATA;
  }

  const teams = Array.isArray(playersProp.teams) && playersProp.teams.length
    ? playersProp.teams.map((team, index) => ({
      id: team && team.id ? team.id : `team-${index + 1}`,
      label: team && team.label ? team.label : `Equipo ${index + 1}`
    }))
    : DEFAULT_PLAYERS_DATA.teams;

  const statKeys = Array.isArray(playersProp.statKeys) && playersProp.statKeys.length
    ? playersProp.statKeys
    : DEFAULT_STAT_KEYS;

  const playersByTeamRaw = isObject(playersProp.playersByTeam) ? playersProp.playersByTeam : DEFAULT_PLAYERS_DATA.playersByTeam;
  const playersByTeam = {};

  teams.forEach((team) => {
    const rawSections = playersByTeamRaw[team.id];
    const normalizedSections = normalizeSections(rawSections, statKeys);
    playersByTeam[team.id] = normalizedSections.length ? normalizedSections : getDefaultSectionsByTeam();
  });

  const selectedTeamId = teams.some(team => team.id === playersProp.selectedTeamId)
    ? playersProp.selectedTeamId
    : (teams[0] ? teams[0].id : null);

  const legend = Array.isArray(playersProp.legend) && playersProp.legend.length
    ? playersProp.legend
    : DEFAULT_LEGEND;

  return {
    teams,
    selectedTeamId,
    playersByTeam,
    legend,
    statKeys
  };
};

class MatchDetailPlayers extends Component {
  constructor(props) {
    super(props);
    const normalizedData = normalizePlayersData(props.players);
    this.state = {
      selectedTeamId: normalizedData.selectedTeamId,
      playersFromApi: null
    };
    this.leagueId = props.leagueId;
    this.matchId = props.matchId;
    this._isMounted = false;
    this._playersRequestId = 0;
    this.players = props.data;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate(prevProps, prevState) {
    const leagueChanged = prevProps.leagueId !== this.props.leagueId;
    const matchChanged = prevProps.matchId !== this.props.matchId;

    if (leagueChanged || matchChanged) {
      this.leagueId = this.props.leagueId;
      this.matchId = this.props.matchId;
    }

    const prevPlayers = this.getPlayersToRender(prevProps, prevState);
    const currentPlayers = this.getPlayersToRender();

    if (prevPlayers !== currentPlayers) {
      const normalizedData = normalizePlayersData(currentPlayers);
      const teamStillExists = normalizedData.teams.some(team => team.id === this.state.selectedTeamId);

      if (!teamStillExists) {
        this.setState({ selectedTeamId: normalizedData.selectedTeamId });
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchPlayersFromApi = async () => {
    if (!this.leagueId || !this.matchId) return;

    const requestId = ++this._playersRequestId;
    const view = 'players';

    try {
      const matchPlayers = await getMatchesStatistics(this.leagueId, this.matchId, view);
      if (!this._isMounted || requestId !== this._playersRequestId) return;

      const mappedPlayers = buildPlayersData(matchPlayers);
      this.setState({ playersFromApi: mappedPlayers || null });
    } catch (error) {
      if (!this._isMounted || requestId !== this._playersRequestId) return;
      this.setState({ playersFromApi: null });
    }
  }

  getPlayersToRender = (propsArg, stateArg) => {
    const props = propsArg || this.props;
    const state = stateArg || this.state;
    const { playersFromApi } = state;

    if (Array.isArray(playersFromApi) || isObject(playersFromApi)) {
      return playersFromApi;
    }

    return props.players;
  }

  handleTeamChange = (teamId) => {
    this.setState({ selectedTeamId: teamId });
  };

  render() {
    const players = this.players;
    const { selectedTeamId } = this.state;
    const normalizedData = normalizePlayersData(players);
    const activeTeamId = normalizedData.teams.some(team => team.id === selectedTeamId)
      ? selectedTeamId
      : normalizedData.selectedTeamId;
    const sections = normalizedData.playersByTeam[activeTeamId] || [];

    return (
      <div className="match-detail-players">
        <MatchDetailPlayersTeamSwitch
          teams={normalizedData.teams}
          selectedTeamId={activeTeamId}
          onChange={this.handleTeamChange}
        />
        <div className="match-detail-players__sections">
          {sections.map((section, index) => (
            <MatchDetailPlayersSection
              key={section.role + '-' + index}
              title={section.role}
              players={section.players}
              statKeys={normalizedData.statKeys}
            />
          ))}
        </div>
        <MatchDetailPlayersLegend items={normalizedData.legend} />
      </div>
    );
  }
}

MatchDetailPlayers.propTypes = {
  players: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  leagueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

MatchDetailPlayers.defaultProps = {
  players: null,
  leagueId: null,
  matchId: null
};

export default MatchDetailPlayers;



// WEBPACK FOOTER //
// ./src/containers/Mundial2026/FootballMatchDetail/Components/MatchDetailPlayers/MatchDetailPlayers.jsx